import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { HtmlEntityDecoderService } from '../../core/services/html-entity-decoder.service';

export interface Order {
  id: string;
  publicOrderCode: string;
  customerName: string;
  items: { name: string; quantity: number }[];
  timestamp: Date;
  total: string; // from backend orderTotalCurrentGross
  status:
    | 'incoming'
    | 'accepted'
    | 'declined'
    | 'preparing'
    | 'delivery'
    | 'completed';
  orderType?: 'delivery' | 'collection'; // ⬅️ NEW
  // UI Flags
  _busy?: boolean;
  _error?: string | null;
}

// --- Backend response types ---
interface PreAuthPayment {
  id: string;
  publicOrderCode: string;
  orderTotalCurrentGross: string;
  orderStatus: string; // e.g. "pending" | "confirmed" | "cooking" | ...
  updatedAt: string; // "YYYY-MM-DD HH:mm:ss"
  orderType?: string; // "delivery" | "collection"
}

interface PreAuthResponse {
  status: string;
  preAuthPayments?: {
    pending?: PreAuthPayment[];
  };
}

/** New list endpoint (shape is inferred; adjust keys if your API differs) */
type Buckets<T> = {
  active?: T[];
  completed?: T[];
  cancelled?: T[];
  preparing?: T[];
  confirmed?: T[];
};

interface OrderListItem {
  id: string;
  publicOrderCode: string;
  orderTotalCurrentGross?: string;
  total?: string;
  orderStatus?: string; // "confirmed" | "cooking" | "completed" | "cancelled" | ...
  updatedAt?: string;
  orderType?: string;
}

interface OrderListResponse {
  status: string;

  /** NEW: actual shape you’re receiving now */
  accepted?: {
    confirmed?: PreAuthPayment[];
    cooking?: PreAuthPayment[];
    completed?: PreAuthPayment[]; // future-friendly
    cancelled?: PreAuthPayment[]; // future-friendly
  };

  /** Transitional (older variant you saw earlier) */
  preAuthPayments?: {
    confirmed?: PreAuthPayment[];
    cooking?: PreAuthPayment[];
    completed?: PreAuthPayment[];
    cancelled?: PreAuthPayment[];
  };

  // Legacy fallbacks
  orderList?: Buckets<OrderListItem>;
  orders?: Buckets<OrderListItem>;
}

type OrderStatusApi = 'cooking' | 'delivery' | 'completed';

type ApiStatus = 'success' | 'failed';

interface ApiResponse {
  status: ApiStatus;
  message?: string;
}

export interface OrderPauseDto {
  tenantId: number;
  isClosedDelivery: 0 | 1;
  isClosedCollection: 0 | 1;
  pauseStartAt?: string; // "HH:mm:ss"
  pauseUntil?: string | null; // "HH:mm:ss" (or null)
  setByUserId?: number;
}

export type ViewOrderPauseOwnerResponse = ApiResponse & {
  data?: OrderPauseDto[];
};

export interface FullOrderListFilters {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  orderBy?: FullOrderListOrderBy;
  orderType?: FullOrderListOrderType | '';
  orderStatus?: FullOrderListOrderStatus | '';
  paymentStatus?: FullOrderListPaymentStatus | '';
}

export type FullOrderListOrderBy = 'ASC' | 'DESC';

export type FullOrderListOrderType = 'delivery' | 'collection';

export type FullOrderListOrderStatus =
  | 'pending'
  | 'confirmed'
  | 'cooking'
  | 'delivery'
  | 'completed'
  | 'cancelled';

export type FullOrderListPaymentStatus =
  | 'unpaid'
  | 'payment_intent'
  | 'paid'
  | 'refunded'
  | 'partial_refund'
  | 'payment_pre_auth';

export interface FullOrderListOrder {
  id: number;
  publicOrderCode: string;
  orderStatus: FullOrderListOrderStatus;
  orderType: FullOrderListOrderType;
  orderTotalCurrentGross: string;
  paymentStatus: FullOrderListPaymentStatus;
  comment: string | null;
  tenantComment: string | null;
  createdAt: string;
  createdAtLocal: string;
}

export interface FullOrderListResponse {
  status: ApiStatus;
  message?: string;
  orders: FullOrderListOrder[];
  total: number;
  action?: string;
}

export interface ViewOrderDeliveryAddress {
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  county: string | null;
  country: string;
  postal_code: string;
}

export interface ViewOrderExtra {
  extraName: string;
  extraPrice: string;
  extraQuantity: number;
}

export interface ViewOrderItem {
  itemName: string;
  itemTotalGrossModified: string;
  itemTotalGrossOriginal: string;
  itemBasePrice: string;
  itemQuantity: number;
  itemNote: string | null;
  itemStatus: string;
  itemTenantComment: string | null;
  updatedAt: string;
  categoryName: string;
  foodOrderItemId: number;
  updatedAtDisplay: string;
  extras: ViewOrderExtra[];
}

export interface ViewOrderDetails {
  id: number;
  publicOrderCode: string;
  orderStatus: FullOrderListOrderStatus;
  orderType: FullOrderListOrderType;
  orderTotalCurrentGross: string;
  paymentStatus: FullOrderListPaymentStatus;
  comment: string | null;
  tenantComment: string | null;
  deliveryAddressId?: number;
  createdAt: string;
  createdAtDisplay: string;
  delivery?: ViewOrderDeliveryAddress;
  details: ViewOrderItem[];
}

export interface ViewOrderByPublicCodeResponse {
  status: ApiStatus;
  message?: string;
  order?: {
    status: ApiStatus;
    orderDetails: ViewOrderDetails[];
  };
  action?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly adminApi = `${environment.orderUrl}`;
  private readonly tenantAPI = `${environment.tenantConfigUrl}`;

  private _orders = new BehaviorSubject<Order[]>([]);
  readonly orders$ = this._orders.asObservable();

  private _orderDetailsCache = new Map<string, any>();

  constructor(
    private http: HttpClient,
    private htmlEntityDecoder: HtmlEntityDecoderService,
  ) {}

  /** Compatibility alias */
  refresh() {
    this.refreshAll();
  }

  /** Fetch pending (pre-auth) + accepted/cooking/completed (order list) and merge */
  refreshAll() {
    forkJoin({
      pending: this.fetchPending$(),
      lists: this.fetchOrderList$(), // { active, completed, cancelled }
    })
      .pipe(
        map(({ pending, lists }) => {
          const fromLists = [
            ...lists.active,
            ...(lists.delivery ?? []),
            ...lists.completed,
          ];

          // De‑dupe by id, prefer the list entries (more up-to-date than pending)
          const taken = new Set(fromLists.map((o) => o.id));
          const onlyPending = pending.filter((o) => !taken.has(o.id));

          // Priority: preparing (cooking/delivery) above accepted (confirmed)
          const weight = (o: Order) => {
            switch (o.status) {
              case 'preparing':
                return 0;
              case 'delivery':
                return 1;
              case 'accepted':
                return 2;
              case 'incoming':
                return 3;
              case 'completed':
                return 4;
              case 'declined':
                return 5;
              default:
                return 9;
            }
          };

          const all = [...onlyPending, ...fromLists].sort((a, b) => {
            const dw = weight(a) - weight(b);
            return dw !== 0 ? dw : +b.timestamp - +a.timestamp;
          });

          return all;
        }),
        catchError((err) => {
          console.error('Failed to refresh orders', err);
          return of([] as Order[]);
        }),
      )
      .subscribe((orders) => this._orders.next(orders));
  }

  /** Accept a pre-authorised order */
  acceptOrder(orderId: string) {
    return this.setPreAuthDecision$(orderId, 1);
  }

  /** Decline a pre-authorised order */
  declineOrder(orderId: string) {
    return this.setPreAuthDecision$(orderId, 0);
  }

  getOrdersByStatus(_status: Order['status']) {
    return this.orders$;
  }

  // ---------- FETCHERS ----------

  /** Fetch only pending pre-auth orders */
  private fetchPending$() {
    return this.http
      .post<PreAuthResponse>(this.adminApi, { action: 'getPreAuthPayments' })
      .pipe(
        map((res) => res?.preAuthPayments?.pending ?? []),
        map((payments) => payments.map((p) => this.mapPreAuthToOrder(p))),
        catchError((err) => {
          console.error('Failed to load pre-auth payments', err);
          return of([] as Order[]);
        }),
      );
  }

  /** Fetch accepted/cooking/completed/cancelled (new “accepted” container; fallbacks included) */
  private fetchOrderList$() {
    return this.http
      .post<OrderListResponse>(this.adminApi, { action: 'getOrderList' })
      .pipe(
        map((res) => {
          // Preferred: "accepted" container
          const container = res?.accepted ?? res?.preAuthPayments ?? null;

          if (container) {
            const cooking = (container as any).cooking ?? [];
            const confirmed = (container as any).confirmed ?? [];
            const delivery = (container as any).delivery ?? []; // NEW
            const completed = (container as any).completed ?? [];

            // Map + internal ordering: cooking before confirmed (both are "active")
            const cookingOrders = (cooking as PreAuthPayment[])
              .map((p) => this.mapPreAuthToStatus(p, 'preparing'))
              .sort((a, b) => +b.timestamp - +a.timestamp);

            const confirmedOrders = (confirmed as PreAuthPayment[])
              .map((p) => this.mapPreAuthToStatus(p, 'accepted'))
              .sort((a, b) => +b.timestamp - +a.timestamp);

            const deliveryOrders = (delivery as PreAuthPayment[])
              .map((p) => this.mapPreAuthToStatus(p, 'delivery'))
              .sort((a, b) => +b.timestamp - +a.timestamp);

            const completedOrders = (completed as PreAuthPayment[]).map((p) =>
              this.mapPreAuthToStatus(p, 'completed'),
            );

            return {
              active: [...cookingOrders, ...confirmedOrders],
              delivery: deliveryOrders,
              completed: completedOrders,
            };
          }

          // Fallback to legacy shapes if present
          const legacy = this.extractOrderListBuckets(res);
          return {
            active: (legacy.active ?? []).map((p) =>
              this.mapOrderListToOrder(p, 'active'),
            ),
            delivery: [],
            completed: (legacy.completed ?? []).map((p) =>
              this.mapOrderListToOrder(p, 'completed'),
            ),
          };
        }),
        catchError((err) => {
          console.error('Failed to load order list', err);
          return of({
            active: [] as Order[],
            delivery: [] as Order[],
            completed: [] as Order[],
          });
        }),
      );
  }

  // ---------- ACTIONS ----------

  /**
   * Calls backend:
   * { action: "acceptedRejectPreAuthPayment", foodOrder: [{ id: number, accepted: 1|0 }] }
   * Backend expects ints for both id and accepted.
   */
  private setPreAuthDecision$(orderId: string, accepted: 1 | 0) {
    const prev =
      this._orders.value.find((o) => o.id === orderId)?.status ?? 'incoming';

    const optimistic: Order['status'] =
      accepted === 1 ? 'accepted' : 'declined';

    // Optimistic UI
    this.markBusy(orderId, true);
    this.updateOrderStatus(orderId, optimistic);

    const payload: any = {
      action: 'acceptedRejectPreAuthPayment',
      foodOrder: [{ id: Number(orderId), accepted }],
    };

    return this.http.post<any>(this.adminApi, payload).pipe(
      // ✅ THIS is “success from backend”
      tap((res) => {
        if (res?.status !== 'success') {
          throw new Error(res?.message ?? 'Accept failed');
        }
      }),

      // Refresh lists after backend success
      tap(() => this.refreshAll()),

      catchError((err) => {
        console.error('Pre-auth decision failed', err);
        this.updateOrderStatus(orderId, prev); // rollback
        this.setError(orderId, 'Failed to update order. Please try again.');
        return throwError(() => err);
      }),

      finalize(() => this.markBusy(orderId, false)),
    );
  }

  /**
   * Update kitchen/delivery status for an order:
   * { action: "updateStatusOrder", foodOrder: [{ id, orderStatus: "cooking"|"delivery"|"completed" }] }
   *
   * - Optimistic mapping:
   *   "cooking" / "delivery" → local status "preparing" (stay in Preparing lane)
   *   "completed" → local status "completed"
   * - After success: re-call acceptedRejectPreAuthPayment (accepted=1) to ensure server-side "accepted" buckets are fresh,
   *   then refresh both feeds.
   */
  private updateStatusOrder(orderId: string, orderStatus: OrderStatusApi) {
    const prev =
      this._orders.value.find((o) => o.id === orderId)?.status ?? 'accepted';

    const optimistic: Order['status'] =
      orderStatus === 'completed'
        ? 'completed'
        : orderStatus === 'delivery'
          ? 'delivery'
          : 'preparing';

    // Optimistic update
    this.markBusy(orderId, true);
    this.updateOrderStatus(orderId, optimistic);

    const payload = {
      action: 'updateStatusOrder',
      foodOrder: [{ id: Number(orderId), orderStatus }],
    };

    this.http
      .post<any>(this.adminApi, payload)
      .pipe(
        catchError((err) => {
          console.error('Failed to update order status', err);
          // Roll back UI state
          this.updateOrderStatus(orderId, prev);
          this.setError(orderId, 'Failed to update order. Please try again.');
          return throwError(() => err);
        }),
        finalize(() => this.markBusy(orderId, false)),
      )
      .subscribe({
        next: () => this.refreshAll(),
        error: () => {
          /* handled */
        },
      });
  }

  // ---------- LOCAL MAPPERS & UTILS ----------

  private updateOrderStatus(orderId: string, newStatus: Order['status']) {
    const current = this._orders.value;
    const updated = current.map((o) =>
      o.id === orderId ? { ...o, status: newStatus } : o,
    );
    this._orders.next(updated);
  }

  private markBusy(orderId: string, busy: boolean) {
    const current = this._orders.value;
    const updated = current.map((o) =>
      o.id === orderId ? { ...o, _busy: busy, _error: null } : o,
    );
    this._orders.next(updated);
  }

  private setError(orderId: string, message: string) {
    const current = this._orders.value;
    const updated = current.map((o) =>
      o.id === orderId ? { ...o, _error: message } : o,
    );
    this._orders.next(updated);
  }

  private mapPreAuthToOrder(p: PreAuthPayment): Order {
    return {
      id: p.id,
      publicOrderCode: p.publicOrderCode,
      customerName: '—',
      items: [],
      total: p.orderTotalCurrentGross ?? '0.00',
      timestamp: this.parseBackendDate(p.updatedAt),
      status: 'incoming',
      orderType: this.normalizeOrderType(p.orderType), // ⬅️ NEW
      _busy: false,
      _error: null,
    };
  }

  /** Reuse pre-auth mapping but force a specific status */
  private mapPreAuthToStatus(
    p: PreAuthPayment,
    status: Order['status'],
  ): Order {
    const base = this.mapPreAuthToOrder(p);
    return { ...base, status, orderType: this.normalizeOrderType(p.orderType) }; // ⬅️ ensure carried
  }

  /** Maps legacy getOrderList items into our Order model */
  private mapOrderListToOrder(
    p: OrderListItem,
    bucket: 'active' | 'completed' | 'cancelled',
  ): Order {
    const derived = this.normalizeStatusFromOrderList(p.orderStatus);
    const status: Order['status'] =
      bucket === 'completed'
        ? 'completed'
        : bucket === 'cancelled'
          ? 'declined'
          : derived;

    return {
      id: String(p.id),
      publicOrderCode: p.publicOrderCode,
      customerName: '—',
      items: [],
      total: p.orderTotalCurrentGross ?? p.total ?? '0.00',
      timestamp: this.parseBackendDate(p.updatedAt || ''),
      status,
      orderType: this.normalizeOrderType(p.orderType), // ⬅️ NEW (may be undefined on real legacy)
      _busy: false,
      _error: null,
    };
  }

  /** Heuristic: map backend orderStatus strings to our local statuses */
  private normalizeStatusFromOrderList(s?: string): Order['status'] {
    const v = (s || '').toLowerCase();
    if (v.includes('cancel')) return 'declined';
    if (v.includes('deliver')) return 'delivery'; // was 'completed' before; now true delivery lane
    if (v.includes('complete')) return 'completed';
    if (v.includes('cook') || v.includes('prep') || v.includes('progress'))
      return 'preparing';
    if (v.includes('confirm') || v.includes('accept') || v.includes('ack'))
      return 'accepted';
    return 'accepted';
  }

  /** Normalize possible legacy shapes into 3 buckets */
  private extractOrderListBuckets(res: OrderListResponse): {
    active: OrderListItem[];
    completed: OrderListItem[];
    cancelled: OrderListItem[];
  } {
    const root =
      (res?.orderList as Buckets<OrderListItem>) ??
      (res?.orders as Buckets<OrderListItem>) ??
      ({} as Buckets<OrderListItem>);

    const active = [
      ...(root.active ?? []),
      ...(root.confirmed ?? []),
      ...(root.preparing ?? []),
    ];
    const completed = root.completed ?? [];
    const cancelled = root.cancelled ?? (root as any).canceled ?? [];

    return { active, completed, cancelled };
  }

  private parseBackendDate(s: string): Date {
    if (!s) return new Date();
    const d = new Date(s.replace(' ', 'T'));
    return isNaN(d.getTime()) ? new Date() : d;
  }

  // ---------- Kitchen helpers (hook UI to these) ----------

  /** Start prep: keeps card in Preparing lane, notifies backend as "cooking" */
  startPreparing(orderId: string) {
    this.updateStatusOrder(orderId, 'cooking');
  }

  markOutForDelivery(orderId: string) {
    this.updateStatusOrder(orderId, 'delivery');
  }

  /** Complete order: moves to Completed lane */
  markCompleted(orderId: string) {
    this.updateStatusOrder(orderId, 'completed');
  }

  /** Fetch details for a single order (cached) */
  viewOrderDetails(orderId: string | number, force = false) {
    const key = String(orderId);

    if (!force) {
      const cached = this._orderDetailsCache.get(key);
      if (cached) return of(cached);
    }

    const payload = {
      action: 'viewOrderDetails',
      orderId: Number(orderId),
      receipt: 'enabled',
    };

    return this.http.post<any>(this.adminApi, payload).pipe(
      map((res) =>
        Array.isArray(res?.orderDetails) ? res.orderDetails[0] : undefined,
      ),
      map((dto) => (dto ? this.htmlEntityDecoder.decodeDeep(dto) : dto)),
      tap((dto) => this._orderDetailsCache.set(key, dto)),
      catchError((err) => {
        console.error('Failed to load order details', err);
        return throwError(() => err);
      }),
    );
  }

  invalidateOrderDetails(orderId: string | number) {
    this._orderDetailsCache.delete(String(orderId));
  }

  /**
   * Poll only "incoming" (pre-auth) orders and merge into the current list
   * without touching accepted/preparing/delivery/completed.
   */
  refreshIncomingOnly$() {
    return this.fetchPending$().pipe(
      tap((pending) => {
        const others = this._orders.value.filter(
          (o) => o.status !== 'incoming',
        );
        const taken = new Set(others.map((o) => o.id));
        const onlyPending = pending.filter((o) => !taken.has(o.id));

        const weight = (o: Order) => {
          switch (o.status) {
            case 'preparing':
              return 0;
            case 'delivery':
              return 1;
            case 'accepted':
              return 2;
            case 'incoming':
              return 3;
            case 'completed':
              return 4;
            case 'declined':
              return 5;
            default:
              return 9;
          }
        };

        const merged = [...others, ...onlyPending].sort((a, b) => {
          const dw = weight(a) - weight(b);
          return dw !== 0 ? dw : +b.timestamp - +a.timestamp;
        });

        this._orders.next(merged);
      }),
      map(() => void 0),
      catchError((err) => {
        console.error('Pending poll failed', err);
        return of(void 0);
      }),
    );
  }

  private normalizeOrderType(
    t?: string,
  ): 'delivery' | 'collection' | undefined {
    const v = (t || '').toLowerCase().trim();
    if (v === 'delivery') return 'delivery';
    if (v === 'collection' || v === 'pickup' || v === 'pick-up')
      return 'collection';
    return undefined; // don't guess if unknown
  }

  insertOrderPause(payload: any) {
    return this.http.post<ApiResponse>(this.tenantAPI, payload).pipe(
      tap((res) => {
        if (!res || res.status !== 'success') {
          throw new Error(res?.message ?? 'Failed to update pause settings');
        }
      }),
      catchError((err) => {
        const msg =
          err?.error?.message ||
          err?.message ||
          'Failed to update pause settings';
        return throwError(() => new Error(msg));
      }),
    );
  }

  viewOrderPauseOwner() {
    const payload = { action: 'viewOrderPauseOwner' };

    return this.http
      .post<ViewOrderPauseOwnerResponse>(this.tenantAPI, payload)
      .pipe(
        tap((res) => {
          if (!res || res.status !== 'success') {
            throw new Error(res?.message ?? 'Failed to load pause status');
          }
        }),
        catchError((err) => {
          const msg =
            err?.error?.message ||
            err?.message ||
            'Failed to load pause status';
          return throwError(() => new Error(msg));
        }),
      );
  }

  deleteOrderPause() {
    const payload = { action: 'deleteOrderPause' };

    return this.http.post<ApiResponse>(this.tenantAPI, payload).pipe(
      tap((res) => {
        if (!res || res.status !== 'success') {
          throw new Error(res?.message ?? 'Failed to resume orders');
        }
      }),
      catchError((err) => {
        const msg =
          err?.error?.message || err?.message || 'Failed to resume orders';
        return throwError(() => new Error(msg));
      }),
    );
  }

  getFullOrderListPaginated(filters: FullOrderListFilters = {}) {
    const payload: any = {
      action: 'getFullOrderListPaginated',
    };

    if (filters.startDate) payload.startDate = filters.startDate;
    if (filters.endDate) payload.endDate = filters.endDate;
    if (filters.limit) payload.limit = filters.limit;
    if (filters.offset !== undefined) payload.offset = filters.offset;
    if (filters.orderBy) payload.orderBy = filters.orderBy;
    if (filters.orderType) payload.orderType = filters.orderType;
    if (filters.orderStatus) payload.orderStatus = filters.orderStatus;
    if (filters.paymentStatus) payload.paymentStatus = filters.paymentStatus;

    return this.http.post<FullOrderListResponse>(this.adminApi, payload).pipe(
      tap((res) => {
        if (!res || res.status !== 'success') {
          throw new Error(res?.message ?? 'Failed to load orders');
        }
      }),
      map((res) => this.htmlEntityDecoder.decodeDeep(res)),
      catchError((err) => {
        const msg =
          err?.error?.message || err?.message || 'Failed to load orders';

        return throwError(() => new Error(msg));
      }),
    );
  }

  viewOrderByPublicCode(publicOrderCode: string) {
    const payload = {
      action: 'viewOrderByPublicCode',
      publicOrderCode,
    };

    return this.http
      .post<ViewOrderByPublicCodeResponse>(this.adminApi, payload)
      .pipe(
        tap((res) => {
          if (!res || res.status !== 'success') {
            throw new Error(res?.message ?? 'Failed to load order');
          }
        }),
        map((res) => this.htmlEntityDecoder.decodeDeep(res)),
        catchError((err) => {
          const msg =
            err?.error?.message || err?.message || 'Failed to load order';

          return throwError(() => new Error(msg));
        }),
      );
  }
}
